export const TD3_SYSEX_PREFIX = [
  0xf0, 0x00, 0x20, 0x32, 0x00, 0x01, 0x0a,
] as const;
export const TD3_SYSEX_END = 0xf7;
export const TD3_PATTERN_DUMP = 0x78;
export const TD3_PATTERN_REQUEST = 0x77;

/**
 * TD-3 pattern dumps always carry 16 step records. This count selects how
 * many of those records are active, matching the hardware FUNCTION+STEP entry.
 */
export const TD3_ACTIVE_STEPS_MIN = 1;
export const TD3_ACTIVE_STEPS_MAX = 16;

/**
 * Raw TD-3 internal-sequencer pitch bytes observed in pattern dumps.
 */
export const TD3_RAW_PITCH_MIN = 0x00;
export const TD3_RAW_PITCH_MAX = 0x2f;

/**
 * Playable TD-3 internal-sequencer pitch bytes for generated writes.
 * This range stays inside the normal keyboard/transpose area. 0x18 is the
 * common default/null C2 value.
 */
export const TD3_PLAYABLE_PITCH_MIN = 0x0c;
export const TD3_PLAYABLE_PITCH_MAX = 0x2f;
export const TD3_PITCH_MIN = TD3_PLAYABLE_PITCH_MIN;
export const TD3_PITCH_MAX = TD3_PLAYABLE_PITCH_MAX;
export const TD3_DEFAULT_PITCH = 0x18;

export type PatternBank = "A" | "B";

export interface Td3Location {
  /** 0-3, displayed as 1-4 */
  group: number;
  /** 0-7, displayed as 1-8 */
  slot: number;
  bank: PatternBank;
}

export interface Td3Step {
  /** TD-3 pattern pitch byte before nibbles are split. Generated writes are validated against the playable range. */
  pitch: number;
  accent: boolean;
  slide: boolean;
  /** True means the TD-3 tie bit is cleared for this step. Tie is a time-mode event, not the same as slide. */
  tie: boolean;
  /** True means the TD-3 rest bit is set for this step. */
  rest: boolean;
}

export interface Td3Pattern {
  activeSteps: number;
  triplet: boolean;
  steps: Td3Step[];
}

export interface PatternDump {
  location: Td3Location;
  pattern: Td3Pattern;
  sysex: Uint8Array;
}

const NOTE_NAMES = [
  "C",
  "C♯",
  "D",
  "D♯",
  "E",
  "F",
  "F♯",
  "G",
  "G♯",
  "A",
  "A♯",
  "B",
];

const PATTERN_GROUP_NAMES = ["I", "II", "III", "IV"] as const;

export function assertLocation(location: Td3Location): void {
  if (
    !Number.isInteger(location.group) ||
    location.group < 0 ||
    location.group > 3
  ) {
    throw new Error(`TD-3 group must be 0-3, got ${location.group}`);
  }
  if (
    !Number.isInteger(location.slot) ||
    location.slot < 0 ||
    location.slot > 7
  ) {
    throw new Error(`TD-3 pattern slot must be 0-7, got ${location.slot}`);
  }
  if (location.bank !== "A" && location.bank !== "B") {
    throw new Error(`TD-3 bank must be A or B, got ${String(location.bank)}`);
  }
}

export function patternNumber(location: Td3Location): number {
  assertLocation(location);
  return location.slot + (location.bank === "B" ? 8 : 0);
}

export function locationFromPatternNumber(
  group: number,
  pattern: number,
): Td3Location {
  if (!Number.isInteger(group) || group < 0 || group > 3) {
    throw new Error(`TD-3 group must be 0-3, got ${group}`);
  }
  if (!Number.isInteger(pattern) || pattern < 0 || pattern > 15) {
    throw new Error(`TD-3 pattern number must be 0-15, got ${pattern}`);
  }
  return { group, slot: pattern % 8, bank: pattern >= 8 ? "B" : "A" };
}

export function locationToIndex(location: Td3Location): number {
  return location.group * 16 + patternNumber(location);
}

export function indexToLocation(index: number): Td3Location {
  if (!Number.isInteger(index) || index < 0 || index > 63) {
    throw new Error(`TD-3 memory index must be 0-63, got ${index}`);
  }
  const group = Math.floor(index / 16);
  const pattern = index % 16;
  return locationFromPatternNumber(group, pattern);
}

export function allLocations(): Td3Location[] {
  return Array.from({ length: 64 }, (_, index) => indexToLocation(index));
}

export function rangeLocations(
  start: Td3Location,
  end: Td3Location,
): Td3Location[] {
  const from = locationToIndex(start);
  const to = locationToIndex(end);
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  return Array.from({ length: hi - lo + 1 }, (_, offset) =>
    indexToLocation(lo + offset),
  );
}

export function locationLabel(location: Td3Location): string {
  assertLocation(location);
  return `Group ${PATTERN_GROUP_NAMES[location.group]} · Pattern ${location.slot + 1}${location.bank}`;
}

export function isPlayableTd3Pitch(value: number): boolean {
  return (
    Number.isInteger(value) &&
    value >= TD3_PLAYABLE_PITCH_MIN &&
    value <= TD3_PLAYABLE_PITCH_MAX
  );
}

export function clampPlayablePitch(value: number): number {
  if (!Number.isFinite(value)) return TD3_DEFAULT_PITCH;
  return Math.max(
    TD3_PLAYABLE_PITCH_MIN,
    Math.min(TD3_PLAYABLE_PITCH_MAX, Math.round(value)),
  );
}

function clampRawPitchByte(value: number): number {
  if (!Number.isFinite(value)) return TD3_DEFAULT_PITCH;
  return Math.max(
    TD3_RAW_PITCH_MIN,
    Math.min(TD3_RAW_PITCH_MAX, Math.round(value)),
  );
}

function nibblePair(value: number): [number, number] {
  if (!Number.isInteger(value) || value < 0 || value > 0xff) {
    throw new Error(
      `Byte value must be 0-255 before nibble split, got ${value}`,
    );
  }
  return [(value & 0xf0) >> 4, value & 0x0f];
}

function readNibblePair(msb: number, lsb: number): number {
  return ((msb & 0x0f) << 4) | (lsb & 0x0f);
}

/** TD-3 stores 16-bit tie/rest maps as low nibbles ordered byte1, byte0, byte3, byte2. */
export function u16ToFourTd3Nibbles(
  value: number,
): [number, number, number, number] {
  if (!Number.isInteger(value) || value < 0 || value > 0xffff) {
    throw new Error(`u16 value expected, got ${value}`);
  }
  return [
    (value & 0x00f0) >> 4,
    value & 0x000f,
    (value & 0xf000) >> 12,
    (value & 0x0f00) >> 8,
  ];
}

export function fourTd3NibblesToU16(
  bytes: ArrayLike<number>,
  offset = 0,
): number {
  return (
    ((bytes[offset] & 0x0f) << 4) |
    (bytes[offset + 1] & 0x0f) |
    ((bytes[offset + 2] & 0x0f) << 12) |
    ((bytes[offset + 3] & 0x0f) << 8)
  );
}

export function validatePattern(pattern: Td3Pattern): void {
  if (
    !Number.isInteger(pattern.activeSteps) ||
    pattern.activeSteps < TD3_ACTIVE_STEPS_MIN ||
    pattern.activeSteps > TD3_ACTIVE_STEPS_MAX
  ) {
    throw new Error(
      `Active steps must be ${TD3_ACTIVE_STEPS_MIN}-${TD3_ACTIVE_STEPS_MAX}, got ${pattern.activeSteps}`,
    );
  }
  if (pattern.steps.length !== TD3_ACTIVE_STEPS_MAX) {
    throw new Error(
      `TD-3 patterns must contain exactly ${TD3_ACTIVE_STEPS_MAX} steps, got ${pattern.steps.length}`,
    );
  }
  pattern.steps.forEach((step, index) => {
    if (!isPlayableTd3Pitch(step.pitch)) {
      throw new Error(
        `Step ${index + 1} pitch must be a playable TD-3 byte 0x${TD3_PLAYABLE_PITCH_MIN.toString(16).toUpperCase().padStart(2, "0")}-0x${TD3_PLAYABLE_PITCH_MAX.toString(16).toUpperCase().padStart(2, "0")}, got ${step.pitch}`,
      );
    }
  });
}

export function buildPatternPayload(
  pattern: Td3Pattern,
  location: Td3Location,
): Uint8Array {
  validatePattern(pattern);
  assertLocation(location);

  const payload: number[] = [
    TD3_PATTERN_DUMP,
    location.group,
    patternNumber(location),
    0x00,
    0x00,
  ];

  for (const step of pattern.steps) {
    payload.push(...nibblePair(step.pitch));
  }

  for (const step of pattern.steps) {
    payload.push(0x00, step.accent ? 0x01 : 0x00);
  }

  for (const step of pattern.steps) {
    payload.push(0x00, step.slide ? 0x01 : 0x00);
  }

  payload.push(0x00, pattern.triplet ? 0x01 : 0x00);
  payload.push(...nibblePair(pattern.activeSteps));
  payload.push(0x00, 0x00);

  let tieMap = 0;
  let restMap = 0;
  pattern.steps.forEach((step, index) => {
    if (!step.tie) {
      tieMap |= 1 << index;
    }
    if (step.rest) {
      restMap |= 1 << index;
    }
  });

  payload.push(...u16ToFourTd3Nibbles(tieMap));
  payload.push(...u16ToFourTd3Nibbles(restMap));

  if (payload.length !== 115) {
    throw new Error(
      `TD-3 pattern payload should be 115 bytes, got ${payload.length}`,
    );
  }

  return Uint8Array.from(payload);
}

export function buildPatternSysEx(
  pattern: Td3Pattern,
  location: Td3Location,
): Uint8Array {
  const payload = buildPatternPayload(pattern, location);
  return Uint8Array.from([...TD3_SYSEX_PREFIX, ...payload, TD3_SYSEX_END]);
}

export function buildPatternRequestSysEx(location: Td3Location): Uint8Array {
  assertLocation(location);
  return Uint8Array.from([
    ...TD3_SYSEX_PREFIX,
    TD3_PATTERN_REQUEST,
    location.group,
    patternNumber(location),
    TD3_SYSEX_END,
  ]);
}

export function isTd3SysEx(data: ArrayLike<number>): boolean {
  if (data.length < TD3_SYSEX_PREFIX.length + 2) return false;
  for (let index = 0; index < TD3_SYSEX_PREFIX.length; index += 1) {
    if (data[index] !== TD3_SYSEX_PREFIX[index]) return false;
  }
  if (data[data.length - 1] !== TD3_SYSEX_END) return false;
  for (let index = 1; index < data.length - 1; index += 1) {
    if (
      !Number.isInteger(data[index]) ||
      data[index] < 0 ||
      data[index] > 0x7f
    ) {
      return false;
    }
  }
  return true;
}

export function isPatternDump(
  data: ArrayLike<number>,
  expected?: Td3Location,
): boolean {
  if (!isTd3SysEx(data)) return false;
  if (data[TD3_SYSEX_PREFIX.length] !== TD3_PATTERN_DUMP) return false;
  if (!expected) return true;
  return (
    data[TD3_SYSEX_PREFIX.length + 1] === expected.group &&
    data[TD3_SYSEX_PREFIX.length + 2] === patternNumber(expected)
  );
}

function decodePitchByte(raw: number): number {
  const withoutHistoricHighBit = raw & 0x7f;
  return clampRawPitchByte(withoutHistoricHighBit);
}

export function decodePatternDump(data: ArrayLike<number>): PatternDump {
  if (!isPatternDump(data)) {
    throw new Error("Not a TD-3 pattern dump SysEx message");
  }
  if (data.length !== 123) {
    throw new Error(
      `TD-3 pattern dump should be 123 bytes including F0/F7, got ${data.length}`,
    );
  }

  const base = TD3_SYSEX_PREFIX.length;
  const group = data[base + 1];
  const patternNumberByte = data[base + 2];
  const location = locationFromPatternNumber(group, patternNumberByte);

  const pitchOffset = base + 5;
  const accentOffset = pitchOffset + 32;
  const slideOffset = accentOffset + 32;
  const tripletOffset = slideOffset + 32;
  const stepCountOffset = tripletOffset + 2;
  const tieOffset = stepCountOffset + 4;
  const restOffset = tieOffset + 4;

  const tieMap = fourTd3NibblesToU16(data, tieOffset);
  const restMap = fourTd3NibblesToU16(data, restOffset);

  const steps: Td3Step[] = Array.from({ length: 16 }, (_, stepIndex) => {
    const dn = stepIndex * 2;
    return {
      pitch: decodePitchByte(
        readNibblePair(data[pitchOffset + dn], data[pitchOffset + dn + 1]),
      ),
      accent: data[accentOffset + dn + 1] === 1,
      slide: data[slideOffset + dn + 1] === 1,
      tie: ((tieMap >> stepIndex) & 1) === 0,
      rest: ((restMap >> stepIndex) & 1) === 1,
    };
  });

  const activeSteps = readNibblePair(
    data[stepCountOffset],
    data[stepCountOffset + 1],
  );
  if (
    activeSteps < TD3_ACTIVE_STEPS_MIN ||
    activeSteps > TD3_ACTIVE_STEPS_MAX
  ) {
    throw new Error(
      `TD-3 pattern dump active steps must be ${TD3_ACTIVE_STEPS_MIN}-${TD3_ACTIVE_STEPS_MAX}, got ${activeSteps}`,
    );
  }

  return {
    location,
    pattern: {
      triplet: data[tripletOffset + 1] === 1,
      activeSteps,
      steps,
    },
    sysex: Uint8Array.from(
      Array.from({ length: data.length }, (_, index) => data[index]),
    ),
  };
}

export function decodePatternDumpFile(data: ArrayLike<number>): PatternDump[] {
  const dumps: PatternDump[] = [];
  let index = 0;
  let sawSysEx = false;

  while (index < data.length) {
    while (index < data.length && data[index] !== TD3_SYSEX_PREFIX[0]) {
      index += 1;
    }
    if (index >= data.length) break;

    sawSysEx = true;
    const start = index;
    while (index < data.length && data[index] !== TD3_SYSEX_END) {
      index += 1;
    }
    if (index >= data.length) {
      throw new Error("Backup file contains an incomplete SysEx message.");
    }

    const end = index + 1;
    const message = Uint8Array.from(
      Array.from({ length: end - start }, (_, offset) => data[start + offset]),
    );
    if (!isPatternDump(message)) {
      throw new Error(
        "Backup file contains a SysEx message that is not a TD-3 pattern dump.",
      );
    }
    dumps.push(decodePatternDump(message));
    index = end;
  }

  if (!sawSysEx) {
    throw new Error("Backup file does not contain any SysEx messages.");
  }
  if (dumps.length === 0) {
    throw new Error("Backup file does not contain TD-3 pattern dumps.");
  }

  return dumps;
}

export function concatSysex(messages: Uint8Array[]): Uint8Array {
  const totalLength = messages.reduce(
    (total, message) => total + message.length,
    0,
  );
  const output = new Uint8Array(totalLength);
  let offset = 0;
  for (const message of messages) {
    output.set(message, offset);
    offset += message.length;
  }
  return output;
}

export function bytesToHex(bytes: ArrayLike<number>, groupEvery = 16): string {
  const parts: string[] = [];
  for (let index = 0; index < bytes.length; index += 1) {
    parts.push(bytes[index].toString(16).toUpperCase().padStart(2, "0"));
    if (
      groupEvery > 0 &&
      index !== bytes.length - 1 &&
      (index + 1) % groupEvery === 0
    ) {
      parts.push("\n");
    }
  }
  return parts.join(" ").replace(/ \n /g, "\n");
}

export function pitchName(pitch: number): string {
  const safe = clampRawPitchByte(pitch);
  const semitone = ((safe % 12) + 12) % 12;
  // TD-3 pattern value 0x18 is documented as C2, one octave below the usual MIDI display convention.
  const octave = Math.floor(safe / 12);
  return `${NOTE_NAMES[semitone]}${octave}`;
}

function timeToken(step: Td3Step): string {
  if (step.tie && step.rest) return "TR";
  if (step.rest) return "RE";
  if (step.tie) return "TI";
  return "  ";
}

export function patternSummary(pattern: Td3Pattern): string {
  const active = pattern.steps.slice(0, pattern.activeSteps);
  const noteStarts = active.filter((step) => !step.rest && !step.tie).length;
  const notRest = active.filter((step) => !step.rest).length;
  const accents = active.filter((step) => step.accent).length;
  const slides = active.filter((step) => step.slide).length;
  const ties = active.filter((step) => step.tie).length;
  return `${pattern.activeSteps}${pattern.triplet ? "T" : ""} steps · ${noteStarts} hits · ${notRest} not-rest · ${accents} accents · ${slides} slides · ${ties} ties`;
}

export function textPattern(
  pattern: Td3Pattern,
  location?: Td3Location,
): string {
  const header = location
    ? `TD-3 JunkMem Pattern ${locationLabel(location)}`
    : "TD-3 JunkMem Pattern";
  const rows = [
    header,
    `Active Steps: ${pattern.activeSteps}, Triplet Time: ${pattern.triplet ? "On" : "Off"}`,
    "",
    `Step:      ${pattern.steps.map((_, index) => String(index + 1).padStart(2, "0")).join(", ")}`,
    `Pitch:     ${pattern.steps.map((step) => pitchName(step.pitch).padStart(3, " ")).join(", ")}`,
    `Accent:    ${pattern.steps.map((step) => (step.accent ? "AC" : "  ")).join(", ")}`,
    `Slide:     ${pattern.steps.map((step) => (step.slide ? "SL" : "  ")).join(", ")}`,
    `Tie/Rest:  ${pattern.steps.map((step) => timeToken(step)).join(", ")}`,
  ];
  return rows.join("\n");
}
